import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertiesAPI, videoAPI, socialAPI, subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import VideoGenerationWizard from '../components/VideoGenerationWizard';
import VideoCard from '../components/VideoCard';
import AppLayout from '../components/AppLayout';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, Video, Loader2, Image as ImageIcon, ChevronDown,
  Zap, ArrowRight, Trash2,
} from 'lucide-react';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  // When the property already has rendered videos, we collapse the
  // template grid by default and show "Bereits erstellt" first. That's
  // what returning users almost always come back for. New properties
  // (zero videos) keep the grid expanded as the primary action.
  const [showTemplates, setShowTemplates] = useState(false);
  const [pollingRenders, setPollingRenders] = useState(new Set());
  // Ref-based mirror so the poll-dedupe check can read the latest set
  // synchronously (React state setters are async). Without this, two near-
  // simultaneous calls to pollVideoStatus(sameRenderId). E.g. resume-on-load
  // racing a wizard submit. Would each start a parallel poll loop.
  const activePollsRef = useRef(new Set());
  // Pending setTimeout ids for in-flight polls. Cleared on unmount so a
  // navigation-away doesn't fire stale setState updates ("can't perform
  // a state update on an unmounted component" warnings) or burn API
  // calls for a screen the user already left.
  const pollTimeoutsRef = useRef(new Set());
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel every pending poll timeout. Note: an in-flight axios
      // request can't be cancelled here without an AbortController,
      // but the next setTimeout that would chain another poll never
      // fires, and the mountedRef guard inside `poll` short-circuits
      // setState if the request resolves after unmount.
      for (const id of pollTimeoutsRef.current) clearTimeout(id);
      pollTimeoutsRef.current.clear();
      activePollsRef.current.clear();
    };
  }, []);
  // Template demo videos that failed to load. Show the thumbnail instead.
  const [brokenDemoVideos, setBrokenDemoVideos] = useState(new Set());
  
  // Publishing happens on /social (which has platform / schedule wired up).
  // We only need to know *whether* the user has any social connections here
  // so we can show a friendly hint when they don't.
  const [socialConnections, setSocialConnections] = useState([]);
  // v1.5. Peek at subscription tier so we can render the upgrade
  // banner above the templates for free users (and hide it for paid).
  // Stays null until the API resolves so we don't flash "upgrade" at
  // someone who's already on Solo/Team for half a second.
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  useEffect(() => {
    subscriptionAPI.getStatus()
      .then(r => setSubscriptionTier(r.data?.subscription_tier || 'free'))
      .catch(() => {/* silent. We just won't show the banner */});
  }, []);

  // Description collapse. Justimmo-imported descriptions can be 800+ chars
  // and used to push the templates well below the fold. Default collapsed,
  // user expands inline. Toggle is only rendered when there's actually
  // something to hide (>180 chars OR >2 line breaks. Early bail-out for
  // short descriptions so the chevron doesn't sit there pointlessly).
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    loadPropertyData();
    loadSocialConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPropertyData = async () => {
    try {
      // Removed `contentAPI.getContent`. Captions used to live in their
      // own collection driven by a vestigial "AI Captions" tab. Now
      // captions are persisted directly onto each GeneratedVideo doc
      // by the render path, so the per-video card displays them
      // inline. No separate fetch needed.
      const [propRes, templatesRes, videosRes] = await Promise.all([
        propertiesAPI.getOne(id),
        videoAPI.getTemplates(),
        videoAPI.getVideos(id).catch(() => ({ data: [] })),
      ]);
      setProperty(propRes.data);
      setVideoTemplates(templatesRes.data);
      setGeneratedVideos(videosRes.data);

      // Resume polling for any video that's still in a non-terminal state
      // when the page loads. Without this, a render started in a previous
      // session would show "Wird gerendert" indefinitely until manual refresh.
      const NON_TERMINAL = new Set(['planned', 'waiting', 'rendering', 'processing']);
      (videosRes.data || []).forEach(v => {
        const renderId = v.render_id || v.creatomate_render_id;
        if (renderId && NON_TERMINAL.has(v.status)) {
          pollVideoStatus(renderId);
        }
      });
    } catch (error) {
      toast.error('Failed to load property data');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const loadSocialConnections = async () => {
    try {
      const response = await socialAPI.getAccounts();
      setSocialConnections(response.data.accounts || []);
    } catch (error) {
      // Silent: this is a secondary load. If it fails the UI already
      // prompts the user to "connect first" when they try to publish.
      // 401 is handled by the global axios interceptor.
    }
  };

  const handlePublish = (contentId, videoId = null) => {
    // Publish happens on the /social page where platform + schedule are
    // actually wired up. Route there with the content / video ids preserved
    // so the SocialMediaPage can pre-fill the publish form.
    if (socialConnections.length === 0) {
      toast.error('Please connect a social media account first');
    }
    navigate('/social', {
      state: { content_id: contentId, video_id: videoId, property_id: id },
    });
  };

  const handleGenerateVideo = async (templateId) => {
    const template = videoTemplates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    setShowWizard(true);
  };

  const confirmGenerateVideo = async (videoData) => {
    setGeneratingVideo(true);
    setShowWizard(false);
    
    try {
      const response = await videoAPI.generateVideo(videoData);
      const renderId = response.data.render_id;
      
      // Add to generated videos list with processing status. The
      // caption + hashtags returned by the backend are stashed locally
      // so the video card can show them the moment polling flips to
      // succeeded. Without this, the freshly-rendered card had no
      // caption until a page reload (the persisted record is only
      // re-fetched on mount).
      const newVideo = {
        id: response.data.id,
        render_id: renderId,
        template_id: videoData.template_id,
        status: 'planned',
        progress: 5,
        startedAt: Date.now(),
        thumbnail_url: property.images?.[0] || '',
        video_url: null,
        caption: response.data.caption || null,
        hashtags: response.data.hashtags || [],
      };
      setGeneratedVideos(prev => [newVideo, ...prev]);

      toast.success('Video wird erstellt. Fortschritt unten.');

      // Start polling for status
      pollVideoStatus(renderId);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video');
    } finally {
      setGeneratingVideo(false);
      setSelectedTemplate(null);
    }
  };

  const stopPolling = (renderId) => {
    activePollsRef.current.delete(renderId);
    setPollingRenders(prev => {
      const next = new Set(prev);
      next.delete(renderId);
      return next;
    });
  };

  const pollVideoStatus = async (renderId) => {
    // De-duplicate: don't start a new loop if one's already running for this id.
    if (activePollsRef.current.has(renderId)) return;
    activePollsRef.current.add(renderId);
    setPollingRenders(prev => new Set(prev).add(renderId));

    const maxAttempts = 60; // 5 minutes with 5 second intervals
    let attempts = 0;

    // Schedule a poll iteration via setTimeout, tracking the id so the
    // unmount cleanup can cancel pending iterations.
    const schedule = (delayMs) => {
      const id = setTimeout(() => {
        pollTimeoutsRef.current.delete(id);
        if (!mountedRef.current) return;  // user navigated away
        if (!activePollsRef.current.has(renderId)) return;  // poll was stopped
        poll();
      }, delayMs);
      pollTimeoutsRef.current.add(id);
    };

    const poll = async () => {
      try {
        const response = await videoAPI.getVideoStatus(renderId);
        // Resolved after unmount? Don't touch React state. The cleanup
        // already wiped active polls and we'd just be triggering the
        // dev-only "setState on unmounted component" warning + a wasted
        // re-render path.
        if (!mountedRef.current) return;
        const statusData = response.data;

        // Update video in list. Also capture `progress` so the card's
        // live bar/ETA reflect the latest backend lifecycle stage.
        setGeneratedVideos(prev =>
          prev.map(v =>
            v.render_id === renderId
              ? {
                  ...v,
                  status: statusData.status,
                  video_url: statusData.video_url,
                  thumbnail_url: statusData.thumbnail_url || v.thumbnail_url,
                  progress: statusData.progress ?? v.progress,
                  error_message: statusData.error_message || v.error_message,
                }
              : v
          )
        );

        if (statusData.status === 'succeeded') {
          toast.success('Video generated successfully!');
          stopPolling(renderId);
          return;
        } else if (statusData.status === 'failed') {
          toast.error('Video generation failed: ' + (statusData.error_message || 'Unknown error'));
          stopPolling(renderId);
          return;
        }

        // Continue polling if still processing
        attempts++;
        if (attempts < maxAttempts) {
          schedule(5000);
        } else {
          // Mark the video itself as timed-out so its card stops spinning.
          // Without this update the card kept showing the "processing"
          // state forever despite the toast firing.
          setGeneratedVideos(prev =>
            prev.map(v =>
              v.render_id === renderId
                ? { ...v, status: 'timeout' }
                : v
            )
          );
          toast.error('Video braucht länger als erwartet. Bitte Seite aktualisieren.');
          stopPolling(renderId);
        }

      } catch (error) {
        if (!mountedRef.current) return;
        // Surface the network failure so the user knows why polling stopped.
        // 401 will already have redirected via the global interceptor.
        if (error?.response?.status !== 401) {
          console.error('Error polling video status:', error);
          toast.error('Status-Check fehlgeschlagen, bitte Seite aktualisieren.');
        }
        setGeneratedVideos(prev =>
          prev.map(v =>
            v.render_id === renderId && v.status !== 'succeeded'
              ? { ...v, status: 'failed' }
              : v
          )
        );
        stopPolling(renderId);
      }
    };

    // Start polling after 5 seconds
    schedule(5000);
  };

  // Delete the property. Imported listings (Justimmo/Willhaben) get a
  // server-side tombstone so automatic syncs don't re-import them — the
  // Datenquellen page lists tombstones with a "Wieder importieren" action.
  const [deleting, setDeleting] = useState(false);
  const handleDeleteProperty = async () => {
    if (!property || deleting) return;
    const imported = !!(property.justimmo_id || property.willhaben_id);
    const msg = imported
      ? `"${property.title}" wirklich löschen?\n\nGeplante Auto-Posts dazu werden gestoppt und das Inserat wird NICHT mehr automatisch importiert. Du kannst es später unter Datenquellen wieder freigeben.`
      : `"${property.title}" wirklich löschen?\n\nGeplante Auto-Posts dazu werden gestoppt.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const res = await propertiesAPI.delete(property.id);
      toast.success(
        res.data?.import_blocked
          ? 'Immobilie gelöscht. Sie wird nicht mehr automatisch importiert.'
          : 'Immobilie gelöscht.'
      );
      navigate('/properties');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen. Bitte erneut versuchen.');
      setDeleting(false);
    }
  };

  // Pull a property in/out of the auto-post pool (e.g. it sold, or the user
  // doesn't want it auto-posted). Manual renders are unaffected. Optimistic
  // update with rollback on failure.
  const [togglingAutopost, setTogglingAutopost] = useState(false);
  const toggleAutopostExclusion = async () => {
    if (!property || togglingAutopost) return;
    const next = !property.excluded_from_autopost;
    setTogglingAutopost(true);
    setProperty(p => ({ ...p, excluded_from_autopost: next }));
    try {
      await propertiesAPI.update(property.id, { excluded_from_autopost: next });
      toast.success(
        next
          ? 'Aus dem Auto-Posting entfernt. Diese Immobilie wird nicht mehr automatisch gepostet.'
          : 'Wieder im Auto-Posting. Diese Immobilie kann wieder automatisch gepostet werden.'
      );
    } catch (e) {
      setProperty(p => ({ ...p, excluded_from_autopost: !next }));
      toast.error('Konnte die Einstellung nicht speichern. Bitte erneut versuchen.');
    } finally {
      setTogglingAutopost(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#6f6a5c]">Loading property...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div>
        <div className="flex items-center justify-between mb-6 gap-2">
          <Button
            onClick={() => navigate('/properties')}
            variant="ghost"
            className="text-[#6f6a5c] hover:text-[#23211a]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zu allen Immobilien
          </Button>
          <Button
            onClick={handleDeleteProperty}
            disabled={deleting}
            variant="outline"
            className="border-[#b3543f]/50 text-[#b3543f] hover:bg-[#f7e8e3] hover:text-[#a34430]"
            data-testid="delete-property-btn"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Löschen
          </Button>
        </div>

        {/* Property Info. Heights/paddings tuned smaller on mobile so the
            templates section appears closer to the fold. The description
            collapses by default (see `shouldCollapseDesc` below) which is
            the biggest contributor to push-down on Justimmo imports. */}
        <Card className="glass border-[#e3dccb] mb-5 sm:mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <div>
                {property.images && property.images[0] ? (
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-52 sm:h-64 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-52 sm:h-64 bg-[#e9e2d2] rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-14 h-14 sm:w-16 sm:h-16 text-[#9c9585]" />
                  </div>
                )}
                {property.images && property.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {property.images.slice(1, 5).map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${property.title} ${index + 2}`}
                        className="w-full h-14 sm:h-20 object-cover rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#23211a] mb-1.5 sm:mb-2 leading-tight">
                  {property.title}
                </h1>
                <p className="text-xl sm:text-2xl text-[#1f4d3a] font-semibold mb-2 sm:mb-3">
                  {property.price}
                </p>
                <p className="text-sm sm:text-base text-[#6f6a5c] mb-3 sm:mb-4">{property.location}</p>

                {/* Collapsible description. Short descriptions render full,
                    long ones clamp to 3 lines with a "Mehr anzeigen" toggle.
                    Threshold of 180 chars matches roughly 3 lines at the
                    typical card width. Tweak if real-world copy disagrees. */}
                {property.description && (() => {
                  const desc = property.description;
                  const shouldCollapseDesc = desc.length > 180 || (desc.match(/\n/g) || []).length > 2;
                  return (
                    <div className="mb-3 sm:mb-4">
                      <p
                        className={`text-sm sm:text-base text-[#4a463b] whitespace-pre-line leading-relaxed ${
                          shouldCollapseDesc && !descExpanded ? 'line-clamp-3' : ''
                        }`}
                      >
                        {desc}
                      </p>
                      {shouldCollapseDesc && (
                        <button
                          type="button"
                          onClick={() => setDescExpanded(v => !v)}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-[#1f4d3a] hover:text-[#2e6b50] transition"
                          aria-expanded={descExpanded}
                          data-testid="property-description-toggle"
                        >
                          {descExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${descExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      )}
                    </div>
                  );
                })()}

                {property.highlights && property.highlights.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-[#23211a]">Highlights:</p>
                    <div className="flex flex-wrap gap-2">
                      {property.highlights.map((highlight, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-[#e9efe7] text-[#1f4d3a] rounded-full text-xs sm:text-sm"
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Auto-post pool toggle — pull a sold/unwanted listing out
                    of auto-posting without deleting it. Manual renders still
                    work; this only governs the automatic scheduler. */}
                <div className="mt-4 sm:mt-5 flex items-center justify-between gap-3 p-3 rounded-lg bg-[#fffdf7] border border-[#e3dccb]">
                  <div className="flex items-start gap-2 min-w-0">
                    <Zap className={`w-4 h-4 mt-0.5 flex-shrink-0 ${property.excluded_from_autopost ? 'text-[#8a8474]' : 'text-[#9a6d1f]'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#23211a]">Auto-Posting</p>
                      <p className="text-[11px] text-[#8a8474] leading-snug">
                        {property.excluded_from_autopost
                          ? 'Pausiert. Diese Immobilie wird nicht automatisch gepostet.'
                          : 'Aktiv. Diese Immobilie kann automatisch gepostet werden.'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={!property.excluded_from_autopost}
                    disabled={togglingAutopost}
                    onCheckedChange={toggleAutopostExclusion}
                    data-testid="autopost-exclusion-toggle"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Free-tier upgrade hint. Shown above the templates so a free
            user sees the path to "auto-everything" right where they're
            already engaged with template choice. Tier === null = still
            loading (no banner flash); tier !== free = paid (no banner). */}
        {subscriptionTier === 'free' && (
          <Card className="glass border-[#1f4d3a]/30 bg-[#f0f3ec] mb-6">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="p-2 rounded-lg bg-[#dfe8db] shrink-0">
                    <Sparkles className="w-4 h-4 text-[#1f4d3a]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#23211a]">
                      Render manuell. Oder lass die KI für dich arbeiten
                    </p>
                    <p className="text-xs text-[#6f6a5c] mt-0.5">
                      Mit Autopilot: Posten läuft von allein auf 6 Plattformen, neue Inserate werden automatisch importiert, du gibst per Vorschau-Mail nur frei.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate('/pricing')}
                  className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white shrink-0 self-start sm:self-auto"
                  data-testid="property-detail-upgrade-banner"
                >
                  <Zap className="w-3.5 h-3.5 mr-1.5" />
                  Auf Autopilot upgraden
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bereits erstellt. When this property has rendered videos,
            they appear FIRST (above the templates) since returning users
            almost always come back to grab/post a video, not generate
            another one. */}
        {generatedVideos.length > 0 && (
          <div className="mb-8">
            <div className="flex items-end justify-between mb-3 sm:mb-4 gap-3 flex-wrap">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#23211a] flex items-center gap-2">
                  <Video className="w-5 h-5 sm:w-6 sm:h-6 text-[#1f4d3a]" />
                  Bereits erstellt
                </h3>
                <p className="text-xs sm:text-sm text-[#8a8474] mt-1">
                  {generatedVideos.length} {generatedVideos.length === 1 ? 'Video' : 'Videos'} für diese Immobilie
                </p>
              </div>
              {generatedVideos.some(v => ['planned','waiting','rendering','processing'].includes(v.status)) && (
                <span className="text-xs px-3 py-1.5 rounded-full bg-[#e9efe7] text-[#2e6b50] border border-[#1f4d3a]/30 inline-flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {generatedVideos.filter(v => ['planned','waiting','rendering','processing'].includes(v.status)).length} laufend
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {generatedVideos.map((video) => (
                <VideoCard
                  key={video.id || video.render_id}
                  video={video}
                  templates={videoTemplates}
                  fallbackThumbnail={property.images?.[0] || ''}
                  onPublish={() => handlePublish(null, video.id || video.render_id)}
                  onRetry={() => handleGenerateVideo(video.template_id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Templates section. Adaptive layout:
            * No videos yet → templates grid expanded as the primary CTA
            * Has videos    → templates collapsed under a "Neues Video"
              card so the existing videos (shown above) take focus. */}
        <div className="mb-8">
          {generatedVideos.length === 0 ? (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#23211a] mb-1.5 sm:mb-2 flex items-center gap-2">
                <Video className="w-7 h-7 sm:w-8 sm:h-8 text-[#1f4d3a]" />
                Wähle eine Vorlage
              </h2>
              <p className="text-sm sm:text-base text-[#6f6a5c] mb-4 sm:mb-6">
                Klick eine Vorlage, um daraus ein Video zu erstellen.
              </p>
            </>
          ) : (
            <Card
              className="glass border-[#1f4d3a]/30 hover:border-[#1f4d3a] cursor-pointer transition mb-6"
              onClick={() => setShowTemplates(v => !v)}
              data-testid="property-toggle-templates"
            >
              <CardContent className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#e9efe7] border border-[#1f4d3a]/30 shrink-0">
                    <Sparkles className="w-5 h-5 text-[#1f4d3a]" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-[#23211a]">
                      Neues Video erstellen
                    </h3>
                    <p className="text-xs text-[#6f6a5c]">
                      {showTemplates
                        ? 'Wähle eine Vorlage'
                        : `${videoTemplates.length} Vorlagen verfügbar. Klicken zum Anzeigen`}
                    </p>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-[#6f6a5c] transition-transform ${
                    showTemplates ? 'rotate-180' : ''
                  }`}
                />
              </CardContent>
            </Card>
          )}

          {/* Video Templates grid. Visible when no videos exist OR when
              the user clicks the "Neues Video erstellen" card above. */}
          <div className={`mb-8 ${generatedVideos.length > 0 && !showTemplates ? 'hidden' : ''}`}>
            {videoTemplates.length === 0 ? (
              <Card className="glass border-[#e3dccb] p-8">
                <p className="text-[#6f6a5c] text-center">Loading templates...</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {videoTemplates.map((template) => (
                  <Card
                    key={template.id}
                    data-testid={`video-template-${template.id}`}
                    onClick={() => handleGenerateVideo(template.id)}
                    className="group glass border-[#e3dccb] hover:border-[#1f4d3a] transition-all duration-300 cursor-pointer overflow-hidden md:hover:scale-105 hover:shadow-2xl active:scale-95 md:active:scale-105"
                  >
                    {/* Video Preview. Shorter on mobile so users can see
                        multiple cards without scrolling forever. */}
                    <div className="relative h-[280px] sm:h-[340px] lg:h-[420px] bg-[#fffdf7] overflow-hidden">
                      {template.demo_video && !brokenDemoVideos.has(template.id) ? (
                        <video
                          src={template.demo_video}
                          className="w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                          onMouseEnter={(e) => e.target.play()}
                          onError={() => {
                            // Demo video URL dead or cross-origin blocked.
                            // Fall back to thumbnail so the card isn't a black box.
                            setBrokenDemoVideos(prev => new Set(prev).add(template.id));
                          }}
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <img
                            src={template.thumbnail}
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                          {template.demo_video && brokenDemoVideos.has(template.id) && (
                            <div className="absolute bottom-3 left-3 text-xs text-[#4a463b] bg-black/60 px-2 py-1 rounded">
                              Vorschau nicht verfügbar
                            </div>
                          )}
                        </div>
                      )}
                      {/* Overlay. Always visible on touch devices (where
                          :hover doesn't fire), fades-in on hover for desktop.
                          Without this, mobile users had no indication a card
                          was tappable beyond the cursor disappearing. */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 sm:pb-6 pointer-events-none">
                        <Button className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary text-xs sm:text-sm" size="sm">
                          <Sparkles className="w-4 h-4 mr-2" />
                          Vorlage verwenden
                        </Button>
                      </div>
                      {/* Template badge */}
                      <div className="absolute top-3 right-3 px-3 py-1 bg-[#1f4d3a]/90 backdrop-blur-sm rounded-full text-xs font-semibold text-white">
                        ✨ Premium
                      </div>
                    </div>

                    <CardContent className="p-4 sm:p-5">
                      <h3 className="text-base sm:text-lg font-bold text-[#23211a] mb-1.5 group-hover:text-[#1f4d3a] transition-colors">
                        {template.name}
                      </h3>
                      {/* Template requirements */}
                      <div className="flex flex-wrap gap-1.5 mb-2.5">
                        {/* Media count badge */}
                        <span data-testid={`template-media-${template.id}`} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${
                          template.media_type === 'video' 
                            ? 'bg-[#1f4d3a]/15 text-[#1f4d3a] border-[#1f4d3a]/20'
                            : 'bg-sky-600/15 text-sky-300 border-sky-600/20'
                        }`}>
                          {template.media_type === 'video' ? (
                            <Video className="w-2.5 h-2.5" />
                          ) : (
                            <ImageIcon className="w-2.5 h-2.5" />
                          )}
                          {template.media_count} {template.media_type === 'video' ? 'Videos' : 'Photos'}
                        </span>
                        
                        {/* Voiceover badge */}
                        {template.has_voiceover && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#9a6d1f]/15 text-[#9a6d1f] border border-amber-600/20">
                            🎙️ Voiceover
                          </span>
                        )}
                        
                        {/* Background audio badge */}
                        {template.has_background_audio && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#f5eede] text-[#8f6f3a] border border-[#c7a869]/40">
                            🎵 Music
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#8a8474]">{template.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Video Generation Wizard */}
        {showWizard && selectedTemplate && (
          <VideoGenerationWizard
            property={property}
            template={selectedTemplate}
            onGenerate={confirmGenerateVideo}
            onCancel={() => {
              setShowWizard(false);
              setSelectedTemplate(null);
            }}
            generatingVideo={generatingVideo}
          />
        )}

      </div>
    </AppLayout>
  );
}
